import {
  observable,
  computed,
  autorun,
  extendObservable,
  action,
  toJS
} from 'mobx';
import { Exclude, Expose, serialize, classToPlain } from 'class-transformer';
import {
  simplyDeserialize,
  safeCopy
} from '../../common_components/serialization/common';
import {
  NODE_TYPES,
  EDGE_TYPES,
  MVPS_FEATURES_STATUSES,
  REGULAR_FONT_SIZE,
  SMALL_FONT_SIZE,
  CURRENT_MVP_FONT_SIZE,
  NETWORK_LAYERS,
  CURRENT_MVP_FEATURE_FONT_SIZE
} from './constants';
import {
  nodeId,
  safeMomentDate,
  getBorderColor,
  getBackgroundColor
} from './functions';
import moment from 'moment';
import NetworkEdges from './NetworkEdges';
import VpElementAbstract from './VpElementAbstract';
import isEqual from 'lodash/isEqual';
import { max, uniq } from 'underscore';

// @Exclude and @Expose are used to exclude all class attributes from
// serialization and expose only a group of them
@Exclude()
export default class Mvp extends VpElementAbstract {
  constructor(store, id, isPersisted = true) {
    super(store, id, isPersisted, NODE_TYPES.MVP);
    extendObservable(this, {
      @Expose()
      bmcBoardId: '',

      @Expose()
      priorMvpIds: []
    });
    this.oldPriorMvpIds = [];

    this.bmcBoardPermission = undefined;
  }

  get layer() {
    return NETWORK_LAYERS.PRODUCT;
  }

  @Expose()
  priorMvpsForSelect() {
    return this.priorMvpIds.map(id => {
      const mvp = this.store.getMvp(id);
      return { value: mvp.id, label: mvp.name };
    });
  }

  serialize() {
    const result = super.serialize();
    // result["priorMvpIds"] = result.priorMvpIds.filter( (id) => !this.oldPriorMvpIds.includes(id) );
    return result;
  }

  get nodeMapping() {
    const fontSize = this.currentMvp
      ? CURRENT_MVP_FONT_SIZE
      : REGULAR_FONT_SIZE;
    const mapping = {
      color: {
        border: getBorderColor(this.status),
        background: getBackgroundColor(this.status)
      },
      font: {
        size: fontSize
      },
      bmcBoardPermission: this.bmcBoardPermission
    };
    return { ...super.nodeMapping, ...mapping };
  }

  get linkingNodeMapping() {
    const fontSize = this.currentMvp ? REGULAR_FONT_SIZE : 15;
    const mapping = {
      layer: NETWORK_LAYERS.PRODUCT,
      font: {
        size: fontSize
      },
      isLinkingNode: true
    };
    return { ...this.nodeMapping, ...mapping };
  }

  get edgesMapping() {
    let edges = [
      ...this.edgesFromPriorMvps,
      ...this.edgesFromValueProposition,
      ...this.edgesFromSections
    ];
    if (this.isPersisted) {
      edges.push(this.edgeToBmc);
    }
    return edges;
  }

  get edgesFromPriorMvps() {
    const edges = [];
    const myNodeId = this.nodeMappingId;
    for (const id of this.priorMvpIds) {
      const fromNodeId = this.store.getMvp(id).nodeMappingId;
      edges.push(
        NetworkEdges.generateEdge(EDGE_TYPES.MVP, myNodeId, fromNodeId)
      );
    }
    return edges;
  }

  get edgesFromValueProposition() {
    const edges = [];
    const myNodeId = this.nodeMappingId;
    if (this.hasEdgeToValueProposition()) {
      edges.push(
        NetworkEdges.generateEdge(EDGE_TYPES.VALUE_PROPOSITION, myNodeId)
      );
    }
    return edges;
  }

  get edgesFromSections() {
    const edges = [];
    const myNodeId = this.nodeMappingId;
    for (const section of this.sections) {
      const sectionNodeId = section.linkingNodeMapping.id;
      const edgeType = this.currentMvp
        ? EDGE_TYPES.CURRENT_MVP
        : EDGE_TYPES.MVP_STARTUP_SECTION;
      edges.push(NetworkEdges.generateEdge(edgeType, myNodeId, sectionNodeId));
    }
    return edges;
  }

  get edgeToBmc() {
    const myNodeId = this.nodeMappingId;
    const edgeType = this.currentMvp ? EDGE_TYPES.CURRENT_MVP : EDGE_TYPES.BMC;
    return NetworkEdges.generateEdge(
      edgeType,
      myNodeId,
      this.virtualBmcSection.nodeMappingId
    );
  }

  get hasBmc() {
    return true;
  }

  get currentMvp() {
    const currentMvpId = this.store.currentMvpId;
    return this.store.currentMvpId == this.id;
  }

  get mvpFeatures() {
    return this.store.getMvpFeaturesByMvp(this.id);
  }

  get sections() {
    return this.store.getSectionsByMvp(this.id);
  }

  get valueProposition() {
    return this.store.valueProposition;
  }

  get mvpNumber() {
    const number = this.name.split('MVP')[1];
    if (number != null) {
      return parseInt(number);
    }
    return '';
  }

  set mvpNumber(number) {
    if (number > 0) {
      this.name = 'MVP' + number;
    }
  }

  get priorMvps() {
    try {
      return this.priorMvpIds.map(id => this.store.getMvp(id));
    } catch (err) {
      return [];
    }
  }

  get nextMvps() {
    try {
      return this.store.mvps.filter(el => el.hasPriorMvpId(this.id));
    } catch (err) {
      return [];
    }
  }

  get bmcIgtasksBoardId() {
    return this.store.rootStore.valuePropositionStore.valueProposition
      .igtasksBoardId;
  }

  @action
  update(source) {
    super.update(source);
    if (this.updatedPriorMvps()) {
      const maxPriorMvp = this.priorMvpWithMaxNumber();
      if (maxPriorMvp) {
        maxPriorMvp.updateName();
        maxPriorMvp.persist();
      }
      this.updateNamesOnMvpsSequence();
    }
  }

  updatedPriorMvps() {
    return !isEqual(toJS(this.priorMvpIds), toJS(this.oldPriorMvpIds));
  }

  @action updateName() {
    let newNumber = this.calcNewNumber();
    const mvpsSequence = this.mvpsSequence();
    const availableNumbers = mvpsSequence.map(mvp => mvp.mvpNumber);
    availableNumbers.push(this.mvpNumber);
    while (
      this.store.getMvpByMvpNumber(newNumber) &&
      !availableNumbers.includes(newNumber)
    ) {
      newNumber += 1;
    }
    this.mvpNumber = newNumber;
  }

  calcNewNumber() {
    let maxPriorMvp = this.priorMvpWithMaxNumber();
    let newNumber = 1;
    if (maxPriorMvp) {
      const prevNumber = maxPriorMvp.mvpNumber;
      if (this.primaryUpdateTarget) {
        maxPriorMvp.updateName();
      }
      newNumber = maxPriorMvp.mvpNumber + 1;
      maxPriorMvp.mvpNumber = prevNumber; // Revert name update
    }
    return newNumber;
  }

  @action
  updateNamesOnMvpsSequence() {
    let newNumber = this.mvpNumber + 1;
    const mvpsSequence = this.mvpsSequence();
    const availableNumbers = mvpsSequence.map(mvp => mvp.mvpNumber);
    // Update numbers
    mvpsSequence.forEach(mvp => {
      while (
        this.store.getMvpByMvpNumber(newNumber) &&
        !availableNumbers.includes(newNumber)
      ) {
        newNumber += 1;
      }
      mvp.mvpNumber = newNumber++;
      mvp.persist();
    });
  }

  priorMvpWithMaxNumber() {
    const priorMvpNumbers = this.priorMvps;
    if (priorMvpNumbers.length == 0) {
      return undefined;
    }
    return max(priorMvpNumbers, mvp => mvp.mvpNumber);
  }

  // Return the sequence of mvps that that have this MVP
  // as a predecessor in DFS order.
  mvpsSequence() {
    let sequence = [];
    this.nextMvps.forEach(mvp => {
      sequence.push(mvp);
      sequence = sequence.concat(mvp.mvpsSequence());
    });
    return uniq(sequence);
  }

  updateWithoutPersistance(source) {
    this.setOldBmcEdges();
    super.updateWithoutPersistance(source);
    this.setPriorMvpIdsFromFormSelect(source.priorMvpsForSelect);
    this.updateNameAsPrimaryTarget();
  }

  updateNameAsPrimaryTarget() {
    this.primaryUpdateTarget = true;
    this.updateName();
    this.primaryUpdateTarget = false;
  }

  setOldBmcEdges() {
    if (this.virtualBmcSection != undefined) {
      this.virtualBmcSection.setOldEdgesIds();
    }
  }

  setPriorMvpIdsFromFormSelect(priorMvpsForSelect) {
    // Save old edges ids in all dependent sections
    this.sections.forEach(section => section.setOldEdgesIds());
    this.oldPriorMvpIds = this.priorMvpIds;
    this.priorMvpIds = priorMvpsForSelect.map(m => m.value);
  }

  canBePriorMvpOf(mvp) {
    if (mvp.priorMvpIds.includes(this.id)) {
      return true;
    }
    return !this.isInPriorsPathOf(mvp) && !mvp.isInPriorsPathOf(this);
  }

  isInPriorsPathOf(mvp) {
    if (this.id == mvp.id) {
      return true;
    }
    if (mvp.priorMvpIds.length == 0) {
      return false;
    }
    for (const id of mvp.priorMvpIds) {
      const priorMvp = this.store.getMvp(id);
      if (this.isInPriorsPathOf(priorMvp)) {
        return true;
      }
    }
    return false;
  }

  hasEdgeToValueProposition() {
    return this.priorMvpIds.length == 0;
  }

  removePriorMvpId(id) {
    this.priorMvpIds.remove(id);
  }

  hasPriorMvpId(id) {
    return this.priorMvpIds.includes(id);
  }

  addPriorMvpIds(ids) {
    // Concatenate removing duplications
    this.priorMvpIds = [...new Set([...this.priorMvpIds, ...ids])];
  }

  getSection(sectionType) {
    return this.sections.find(section => section.nodeType == sectionType);
  }

  destroy() {
    // Remove all features from frontend
    this.mvpFeatures.forEach(f => f.removeMvp(this.id));
    this.sections.forEach(section => section.destroyFrontend());
    super.destroy();
  }

  static deserialize(json) {
    let result = simplyDeserialize(Mvp, json);
    result['finishDate'] = safeMomentDate(json.finishDate);
    result['startDate'] = safeMomentDate(json.startDate);
    return result;
  }

  get dependingNodes() {
    return [
      ...this.store.rootStore.mvpFeaturesStore.getFeaturesByMvpId(this.id),
      ...this.store.rootStore.sectionsStore.getSectionsByMvpId(this.id)
    ];
  }
}

Mvp.allowedInstanceProperties = [
  'name',
  'description',
  'igtasksBoardId',
  'bmcBoardId',
  'priorMvpIds',
  'priority',
  'complexity',
  'status',
  'startDate',
  'finishDate',
  'userIds',
  'users',
  'documentationId',
  'igtasksBoardPermission',
  'bmcBoardPermission'
];

/*
    propagateNameChange(){
        const mvpNumber = this.mvpNumber;
        const priorMvps = this.priorMvps;
        for( const mvp of priorMvps ){
            if( mvp.priorMvpIds.length == 1 ){
                const priorMvp2 = mvp.priorMvps[0];
                if( this.hasPriorMvpId(priorMvp2.id) && 
                    priorMvp2.mvpNumber == (this.mvpNumber-1) ){
                    this.swapNames(mvp);
                    this.persist();
                    mvp.persist();
                }
            }
        }
    }

    swapNames(mvp){
        let myName = this.name;
        this.name = mvp.name;
        mvp.name = myName;
    }
*/
